import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { promotionsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { formatMoney } from '@/utils/currency';

interface PromoReport {
  rows: { campaign_name: string; invoices: number; promo_total: number; net_sales: number }[];
  totals: { invoices: number; promo_total: number; net_sales: number };
}

export default function PromotionReportSection() {
  const { t } = useTranslation();
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const { data, isLoading } = useQuery({
    queryKey: ['promotionReport', from_date, to_date],
    queryFn: async () => {
      const res = await promotionsApi.getReport({ from_date, to_date });
      return res.data.message as PromoReport;
    },
  });

  const rows = data?.rows || [];
  const totals = data?.totals || { invoices: 0, promo_total: 0, net_sales: 0 };
  const hasRows = rows.length > 0;
  const summary = useMemo(() => totals, [totals]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
        />
        <Link to="/promotions" className="text-sm text-primary-700 underline">
          {t('promotions.manageSales')}
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card p-4">
          <p className="text-xs text-gray-500">{t('promotions.report.invoices')}</p>
          <p className="text-xl font-bold">{summary.invoices}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500">{t('promotions.report.saved')}</p>
          <p className="text-xl font-bold text-emerald-700">{formatMoney(summary.promo_total)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs text-gray-500">{t('promotions.report.netSales')}</p>
          <p className="text-xl font-bold">{formatMoney(summary.net_sales)}</p>
        </div>
      </div>

      <div className="card overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">{t('promotions.report.sale')}</th>
              <th className="text-right px-4 py-2">{t('promotions.report.invoices')}</th>
              <th className="text-right px-4 py-2">{t('promotions.report.saved')}</th>
              <th className="text-right px-4 py-2">{t('promotions.report.netSales')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  {t('common.loading')}
                </td>
              </tr>
            ) : !hasRows ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  {t('promotions.report.empty')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.campaign_name} className="border-t">
                  <td className="px-4 py-2 font-medium">{row.campaign_name}</td>
                  <td className="px-4 py-2 text-right">{row.invoices}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(row.promo_total)}</td>
                  <td className="px-4 py-2 text-right">{formatMoney(row.net_sales)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
